import * as React from 'react'
import { addDays, endOfDay, max, min, startOfDay } from 'date-fns'
import { CalendarDays, Table2 } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryState } from 'nuqs'
import type { RowSelectionState } from '@tanstack/react-table'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { useDataTable } from '@/components/data-table/data-table-provider'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { OpenSlotsNotice } from '@/features/appointments/components/OpenSlotsNotice'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { columnFiltersToFilterConditions } from '@/features/calls/utils/mergeFilters'
import {
  APPOINTMENTS_PAGE_LIMIT_MAX,
  APPOINTMENTS_TABLE_OPEN_SLOTS_DAYS,
  APPOINTMENTS_TABLE_OPEN_SLOTS_MAX_WINDOWS,
} from '@/config/constants'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import {
  useAppointmentsQuery,
  useAppointmentsRangeQuery,
} from '@/hooks/useAppointmentsQuery'
import { useOpenAvailabilityQuery } from '@/hooks/useOpenAvailabilityQuery'
import { useProviderOptions } from '@/hooks/useProviderOptions'
import { useFacilityOptions } from '@/hooks/useFacilityOptions'
import type {
  AppointmentRecord,
  AvailabilityBlock,
} from '@/features/appointments/types'
import { ImportDataButton } from '@/features/data-import'
import { appointmentsColumns } from '@/features/appointments/components/AppointmentsColumns'
import {
  getAppointmentDate,
  visibleCalendarAppointments,
} from '@/features/appointments/utils/calendarAppointments'
import { AppointmentsCalendar } from '@/features/appointments/components/AppointmentsCalendar'
import { useAppointmentsViewState } from '@/features/appointments/hooks/useAppointmentsViewState'
import { AppointmentsSheetWrapper } from '@/features/appointments/components/AppointmentsSheetDetails'
import {
  createAppointmentsFilterFields,
  appointmentsSchema,
  APPOINTMENTS_TABLE_ID,
  SHOW_OPEN_SLOTS_FIELD,
} from '@/features/appointments/components/appointmentsTableSchema'
import {
  mergeOpenSlotRows,
  resolveShowOpenSlots,
} from '@/features/appointments/utils/openSlotRows'

const TABLE_ID = APPOINTMENTS_TABLE_ID
const DATE_FIELD = 'source_appointment_date'
// The open-availability endpoint filters on start_time (a timestamptz), not
// the appointments-specific source_appointment_date (a bare date) column.
const AVAILABILITY_DATE_FIELD = 'start_time'
// The availability endpoint has no analog for `status` (a slot is
// open/booked/unavailable, not pending/completed/cancelled/booked/
// rescheduled) — dropped from its filters rather than forwarded and rejected.
const AVAILABILITY_UNSUPPORTED_FIELDS = new Set(['status'])
// Stable empty fallbacks so the memoized calendar slot doesn't see a fresh
// array identity on every render while a query has no data.
const NO_APPOINTMENTS: AppointmentRecord[] = []
const NO_OPEN_SLOTS: AvailabilityBlock[] = []
// Table view with no Date filter: if the next 14 days have no open slots, try
// the 14 days after that, and so on (see useOpenAvailabilityQuery).
const TABLE_SLOTS_LOOKAHEAD = {
  windowDays: APPOINTMENTS_TABLE_OPEN_SLOTS_DAYS,
  maxWindows: APPOINTMENTS_TABLE_OPEN_SLOTS_MAX_WINDOWS,
}

/**
 * Sheet slot wrapper: renders the appointment detail sheet and keeps the
 * `details` URL flag in sync with the sheet's open state — set `details=1`
 * while a row is selected, drop it when selection clears, so the flag never
 * lingers after close. Mirrors Call Logs' and Clients' sheet slots; the sync
 * reads `rowSelection`, which only exists in the DataTable context
 * DataTableInfinite mounts.
 */
function AppointmentsSheet({
  error,
  onRetry,
}: {
  error?: Error | null
  onRetry?: () => void
}) {
  const { table, rowSelection } = useDataTable<AppointmentRecord, unknown>()
  const { setFilter } = useFilterActions()
  const [detailsParam, setDetailsParam] = useQueryState('details', {
    history: 'replace',
  })

  const detailsDesired = Object.keys(rowSelection).length > 0 ? '1' : null
  React.useEffect(() => {
    if (detailsParam !== detailsDesired) void setDetailsParam(detailsDesired)
  }, [detailsDesired, detailsParam, setDetailsParam])

  // Re-select when the deep-link id CHANGES after mount. `initialRowSelection`
  // is a useState initialiser, so it only fires on a fresh mount — but tabs
  // stay mounted in a hidden <Activity>, so a react-router navigation to
  // /data/appointments?appointments.id=<uuid> re-renders an already-open
  // Appointments tab without remounting it. Without this the URL would name an
  // appointment while the sheet stayed shut. (Reachable via a notification's
  // primary_action.url, which routes through react-router.)
  //
  // Guarded on a CHANGE to a non-null id, so it never fights the user: in-app
  // row clicks and the sheet's ↑/↓ navigation don't write `id`, and closing
  // sets it to null rather than to another row.
  const storeState = useFilterState()
  const deepLinkId = typeof storeState?.id === 'string' ? storeState.id : null
  const appliedDeepLinkRef = React.useRef(deepLinkId)
  React.useEffect(() => {
    if (deepLinkId && deepLinkId !== appliedDeepLinkRef.current) {
      table.setRowSelection({ [deepLinkId]: true })
    }
    appliedDeepLinkRef.current = deepLinkId
  }, [deepLinkId, table])

  // Dismissing the sheet drops the deep-link id too, so the URL stops naming
  // an appointment that is no longer open — otherwise a reload would reopen
  // the sheet the user just closed, and the table would stay narrowed to that
  // one row with nothing on screen explaining why. Only an explicit dismiss
  // does this (see AppointmentsSheetWrapper's `onClose`).
  const handleClose = React.useCallback(() => {
    setFilter('id', null)
  }, [setFilter])

  return (
    <AppointmentsSheetWrapper
      onClose={handleClose}
      requestError={error}
      onRetry={onRetry}
    />
  )
}

/** Calendar and table share the same selection and details sheet. */
function SelectableAppointmentsCalendar(
  props: React.ComponentProps<typeof AppointmentsCalendar>
) {
  const { table, rowSelection } = useDataTable<AppointmentRecord, unknown>()
  return (
    <AppointmentsCalendar
      {...props}
      selectedAppointmentId={Object.keys(rowSelection)[0]}
      onAppointmentSelect={(appointment) =>
        table.setRowSelection({ [appointment.id]: true })
      }
    />
  )
}

function AppointmentsInner() {
  // Which view is up, and the calendar's range mode and first visible day,
  // all live in the URL (`appointments.view` / `.range` / `.date`) so the
  // selection is visible, survives a reload and can be shared as a link.
  // Defaults — Table, a 3-day window centered on today — write no params;
  // switching range modes recenters on today so "Week" never opens on a
  // stale offset. (The Slots toggle has its own URL-backed home in the filter
  // store; see SHOW_OPEN_SLOTS_FIELD below.)
  const { view, setView, rangeDays, setRangeDays, rangeStart, setRangeStart } =
    useAppointmentsViewState()
  const storeState = useFilterState()
  const { resetAllFilters, setFilter } = useFilterActions()
  const deepLinkId = typeof storeState?.id === 'string' ? storeState.id : null

  // Open the details sheet over a single appointment when the URL carries
  // `?appointments.id=<uuid>`. Rows are keyed by appointment id (see getRowId)
  // and the id below narrows the table query server-side, so the deep-linked
  // row is present to select however old it is.
  //
  // The id ALONE is what opens the sheet — `details=1` is written alongside it
  // for parity with Call Logs and Clients, but is deliberately not required to
  // read it back. That flag is derived from row selection and is cleared
  // whenever nothing is selected, so it is the fragile half of the pair: a
  // `details=1` that arrives without an id is stripped from the URL on load.
  // Hanging the sheet off the durable half keeps a hand-trimmed or
  // partially-copied link working.
  const [initialRowSelection] = React.useState<RowSelectionState>(() =>
    deepLinkId ? { [deepLinkId]: true } : {}
  )

  // Provider/Location are compact multi-select pickers backed by the org's
  // full provider/facility lists (see appointmentsTableSchema.ts) — fetched
  // unconditionally (not gated to a view) since the filter rail is shared
  // between Table and Calendar. Destructured to plain identifiers first —
  // exhaustive-deps can't prove a member expression like
  // `providerOptionsQuery.refetch` is stable across renders.
  const {
    data: providerOptions,
    isLoading: providerOptionsLoading,
    isError: providerOptionsErrored,
    refetch: refetchProviderOptions,
  } = useProviderOptions()
  const {
    data: facilityOptions,
    isLoading: facilityOptionsLoading,
    isError: facilityOptionsErrored,
    refetch: refetchFacilityOptions,
  } = useFacilityOptions()
  const providerOptionsState = React.useMemo(
    () => ({
      state: providerOptionsLoading
        ? ('loading' as const)
        : providerOptionsErrored
          ? ('error' as const)
          : ('ready' as const),
      onRetry: () => void refetchProviderOptions(),
    }),
    [providerOptionsLoading, providerOptionsErrored, refetchProviderOptions]
  )
  const facilityOptionsState = React.useMemo(
    () => ({
      state: facilityOptionsLoading
        ? ('loading' as const)
        : facilityOptionsErrored
          ? ('error' as const)
          : ('ready' as const),
      onRetry: () => void refetchFacilityOptions(),
    }),
    [facilityOptionsLoading, facilityOptionsErrored, refetchFacilityOptions]
  )
  const filterFields = React.useMemo(
    () =>
      createAppointmentsFilterFields(
        providerOptions,
        providerOptionsState,
        facilityOptions,
        facilityOptionsState
      ),
    [
      providerOptions,
      providerOptionsState,
      facilityOptions,
      facilityOptionsState,
    ]
  )

  // Only real filter fields become query params — exclude store metadata (sort,
  // selected-row uuid) so opening the details sheet doesn't churn the query key.
  const columnFilters = React.useMemo(() => {
    const ids = new Set(filterFields.map((f) => String(f.value)))
    return Object.entries(storeState ?? {})
      .filter(([key, value]) => {
        if (!ids.has(key)) return false
        if (value === null || value === undefined) return false
        if (Array.isArray(value)) return value.length > 0
        return String(value).trim().length > 0
      })
      .map(([id, value]) => ({ id, value }))
  }, [storeState, filterFields])

  // The toolbar's Slots toggle, resolved against the active view's default.
  // A deep link narrows the table to one appointment, so no slots there — but
  // the toggle itself still shows the user's setting.
  const slotsValue = storeState?.[SHOW_OPEN_SLOTS_FIELD]
  const slotsToggle = resolveShowOpenSlots(
    typeof slotsValue === 'boolean' ? slotsValue : null,
    view
  )
  const showOpenSlots = slotsToggle && !(view === 'table' && deepLinkId)

  const apiFilters = React.useMemo(() => {
    const conditions = columnFiltersToFilterConditions(columnFilters)

    // `id` is not a user-facing filter field (an internal uuid makes a poor
    // filter chip), so it's appended here rather than via
    // createAppointmentsFilterFields: the deep link narrows the table to
    // exactly that appointment. Deliberately NOT folded into
    // `calendarFilters`/`availabilityFilters` — those are built off
    // `columnFilters`, and the availability endpoint has no `id` at all.
    if (deepLinkId) {
      conditions.push({
        id: 'id-=',
        field: 'id',
        operator: '=',
        value: deepLinkId,
      })
    }
    return conditions
  }, [columnFilters, deepLinkId])

  // Calendar filters: the rail's "Appointment Date" range (Dates in the
  // store) is clamped to the visible range BEFORE serialization, then the
  // one resulting range goes through the shared converter — duplicate
  // filter[field][op] params are structurally impossible. The window is a
  // day wide on each side: the bounds are local-time instants against a
  // bare date column, so this keeps relay's date-cast from dropping the
  // range's first/last day far from UTC (the grid buckets by exact day, so
  // no extra days render). A rail range disjoint from the window clamps to
  // an empty (lower > upper) range — honestly no rows.
  const calendarFilters = React.useMemo(() => {
    const from = startOfDay(addDays(rangeStart, -1))
    const to = endOfDay(addDays(rangeStart, rangeDays))
    const rail = columnFilters.find((f) => f.id === DATE_FIELD)?.value as
      | Date[]
      | undefined
    return columnFiltersToFilterConditions([
      ...columnFilters.filter((f) => f.id !== DATE_FIELD),
      {
        id: DATE_FIELD,
        value: [max([rail?.[0] ?? from, from]), min([rail?.[1] ?? to, to])],
      },
    ])
  }, [columnFilters, rangeStart, rangeDays])

  // Open-availability filters: the same rail filters as the appointments
  // queries, minus `status` (no analog on a slot) and with the date condition
  // remapped to `start_time` (the availability endpoint's column, not
  // `source_appointment_date`). Relay requires both bounds, so the window is:
  // - Calendar: the same clamped, padded window as `calendarFilters` — rows
  //   outside the visible range are dropped when bucketed by day regardless.
  // - Table: the rail's Date range; a missing start is today and a missing
  //   end is APPOINTMENTS_TABLE_OPEN_SLOTS_DAYS from the start. With no Date
  //   filter at all, the query also looks ahead window by window.
  const availabilityFilters = React.useMemo(() => {
    const rail = columnFilters.find((f) => f.id === DATE_FIELD)?.value as
      | Date[]
      | undefined
    let from: Date
    let to: Date
    if (view === 'calendar') {
      const windowFrom = startOfDay(addDays(rangeStart, -1))
      const windowTo = endOfDay(addDays(rangeStart, rangeDays))
      from = max([rail?.[0] ?? windowFrom, windowFrom])
      to = min([rail?.[1] ?? windowTo, windowTo])
    } else {
      from = rail?.[0] ?? startOfDay(new Date())
      to =
        rail?.[1] ??
        endOfDay(addDays(from, APPOINTMENTS_TABLE_OPEN_SLOTS_DAYS - 1))
    }
    return columnFiltersToFilterConditions([
      ...columnFilters.filter(
        (f) => f.id !== DATE_FIELD && !AVAILABILITY_UNSUPPORTED_FIELDS.has(f.id)
      ),
      { id: AVAILABILITY_DATE_FIELD, value: [from, to] },
    ])
  }, [columnFilters, view, rangeStart, rangeDays])

  // Calendar links resolve by ID independently of the current date window.
  // Without a link, only the active view fetches appointments.
  const query = useAppointmentsQuery(
    {
      limit: 50,
      sort_by: DATE_FIELD,
      sort_order: 'desc',
      filters:
        view === 'calendar' && deepLinkId
          ? [{ id: 'id-=', field: 'id', operator: '=', value: deepLinkId }]
          : apiFilters.length > 0
            ? apiFilters
            : undefined,
    },
    { enabled: view === 'table' || !!deepLinkId }
  )

  // Calendar query — a single range-bounded fetch that walks every page in
  // one queryFn (see useAppointmentsRangeQuery), keyed by the clamped filters
  // (which embed the visible range), so range navigation drives refetches.
  const calendarQuery = useAppointmentsRangeQuery(
    {
      limit: APPOINTMENTS_PAGE_LIMIT_MAX,
      sort_by: DATE_FIELD,
      sort_order: 'asc',
      filters: calendarFilters,
    },
    { enabled: view === 'calendar' }
  )

  // Open-availability query — only while Slots is shown in the active view.
  // Relay returns already-coalesced blocks bounded by the requested date
  // window, not raw per-slot rows. Only the table without a Date filter looks
  // ahead past an empty window; the calendar and an explicit Date range always
  // show exactly the requested window.
  const hasDateFilter = columnFilters.some((f) => f.id === DATE_FIELD)
  const openAvailabilityQuery = useOpenAvailabilityQuery(
    {
      filters: availabilityFilters,
      lookahead:
        view === 'table' && !hasDateFilter ? TABLE_SLOTS_LOOKAHEAD : undefined,
    },
    { enabled: showOpenSlots }
  )
  // A disabled query keeps its last data/error, so everything read from it is
  // gated on the toggle.
  const openSlots = showOpenSlots
    ? (openAvailabilityQuery.data?.data ?? NO_OPEN_SLOTS)
    : NO_OPEN_SLOTS
  const openSlotsLoading = showOpenSlots && openAvailabilityQuery.isLoading
  const openSlotsFetching = showOpenSlots && openAvailabilityQuery.isFetching
  const openSlotsError = showOpenSlots ? openAvailabilityQuery.error : null
  const openSlotsTruncated =
    showOpenSlots && (openAvailabilityQuery.data?.truncated ?? false)

  const appointments = React.useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? NO_APPOINTMENTS,
    [query.data]
  )
  // Resolve deep links independently of the visible range and filter rail.
  const linkedAppointment =
    !query.isPlaceholderData && deepLinkId
      ? appointments.find((row) => row.id === deepLinkId)
      : undefined
  const positionedLink = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!deepLinkId) positionedLink.current = null
    if (
      view !== 'calendar' ||
      !linkedAppointment ||
      positionedLink.current === deepLinkId
    )
      return
    positionedLink.current = deepLinkId
    const date = getAppointmentDate(linkedAppointment)
    if (
      date &&
      (date < startOfDay(rangeStart) ||
        date >= addDays(startOfDay(rangeStart), rangeDays))
    ) {
      setRangeStart(date)
    }
  }, [
    view,
    deepLinkId,
    linkedAppointment,
    rangeStart,
    rangeDays,
    setRangeStart,
  ])

  const calendarAppointments = React.useMemo(() => {
    const rows = calendarQuery.data?.appointments ?? NO_APPOINTMENTS
    return linkedAppointment &&
      !rows.some((row) => row.id === linkedAppointment.id)
      ? [...rows, linkedAppointment]
      : rows
  }, [calendarQuery.data, linkedAppointment])
  const visibleAppointments = React.useMemo(
    () =>
      visibleCalendarAppointments(calendarAppointments, rangeStart, rangeDays),
    [calendarAppointments, rangeStart, rangeDays]
  )
  const data = React.useMemo(() => {
    if (view === 'table') return mergeOpenSlotRows(appointments, openSlots)
    // Keep the exact linked row available while its calendar window is moving.
    return linkedAppointment &&
      !visibleAppointments.some((row) => row.id === linkedAppointment.id)
      ? [...visibleAppointments, linkedAppointment]
      : visibleAppointments
  }, [view, appointments, openSlots, visibleAppointments, linkedAppointment])
  const appointmentsTotal = query.data?.pages[0]?.pagination.total
  const totalRows =
    appointmentsTotal == null
      ? undefined
      : appointmentsTotal + data.length - appointments.length

  const getRowId = React.useCallback((row: AppointmentRecord) => row.id, [])

  // Recover from a deep link that resolves to nothing — a deleted appointment,
  // one belonging to another org, or an id pasted alongside filters it doesn't
  // match. `useSheetRowNavigation` drops the unresolvable selection, which
  // clears `details`, but `id` would otherwise stay and keep narrowing the
  // table to zero rows. Because `id` is deliberately not a visible filter
  // field, that leaves "No results" with no chip to clear it. Dropping the id
  // widens the table back to the full list.
  //
  // The `isPlaceholderData` guard is load-bearing: this query sets
  // `placeholderData: keepPreviousData`, so on a filter change `isLoading` is
  // FALSE while the previous filters' rows are still on screen. Acting then
  // would clear the id of a link whose row simply hasn't arrived yet — the
  // deep link would destroy itself mid-load. Only act once the rows on screen
  // actually belong to the current filters.
  const deepLinkUnresolved =
    deepLinkId !== null &&
    !query.isLoading &&
    !query.isFetching &&
    !query.isError &&
    !query.isPlaceholderData &&
    !appointments.some((row) => row.id === deepLinkId)
  React.useEffect(() => {
    if (!deepLinkUnresolved) return
    setFilter('id', null)
    toast.error('That appointment is no longer available')
  }, [deepLinkUnresolved, setFilter])

  // Combined refresh/retry for the active view: its appointments query plus
  // open slots when shown (`refetch` ignores `enabled`, so hidden slots are
  // skipped explicitly). Destructured to plain identifiers first —
  // exhaustive-deps can't prove a member expression like
  // `calendarQuery.refetch` is stable across renders.
  const { refetch: refetchCalendar } = calendarQuery
  const { refetch: refetchTable } = query
  const { refetch: refetchAvailability } = openAvailabilityQuery
  const refreshActiveView = React.useCallback(() => {
    if (view === 'calendar') {
      refetchCalendar()
      if (deepLinkId) refetchTable()
    } else refetchTable()
    if (showOpenSlots) refetchAvailability()
  }, [
    view,
    deepLinkId,
    showOpenSlots,
    refetchCalendar,
    refetchTable,
    refetchAvailability,
  ])

  const selectionError =
    view === 'calendar'
      ? (deepLinkId && query.error) || calendarQuery.error
      : query.error
  const SheetSlot = React.useMemo(
    () => (
      <AppointmentsSheet error={selectionError} onRetry={refreshActiveView} />
    ),
    [selectionError, refreshActiveView]
  )

  const ContentSlot = React.useMemo(
    () =>
      view === 'calendar' ? (
        <SelectableAppointmentsCalendar
          appointments={calendarAppointments}
          openSlots={openSlots}
          rangeStart={rangeStart}
          rangeDays={rangeDays}
          onRangeStartChange={setRangeStart}
          onRangeDaysChange={setRangeDays}
          isLoading={calendarQuery.isLoading || openSlotsLoading}
          isFetching={calendarQuery.isFetching || openSlotsFetching}
          isError={calendarQuery.isError || openSlotsError !== null}
          error={calendarQuery.error ?? openSlotsError}
          onRetry={refreshActiveView}
          truncated={
            (calendarQuery.data?.truncated ?? false) || openSlotsTruncated
          }
        />
      ) : undefined,
    [
      view,
      rangeStart,
      rangeDays,
      setRangeStart,
      setRangeDays,
      calendarAppointments,
      calendarQuery.data?.truncated,
      calendarQuery.isLoading,
      calendarQuery.isFetching,
      calendarQuery.isError,
      calendarQuery.error,
      openSlots,
      openSlotsLoading,
      openSlotsFetching,
      openSlotsError,
      openSlotsTruncated,
      refreshActiveView,
    ]
  )

  // Table view only: surface slot load errors and truncation above the rows.
  // The calendar shows these in its own states (see ContentSlot above).
  const ChartSlot = React.useMemo(
    () =>
      view === 'table' ? (
        <OpenSlotsNotice
          error={openSlotsError}
          truncated={openSlotsTruncated}
          onRetry={() => void refetchAvailability()}
        />
      ) : undefined,
    [view, openSlotsError, openSlotsTruncated, refetchAvailability]
  )

  // Toolbar actions: the Table | Calendar toggle (the same rail/command
  // filter state drives whichever view's query is active), the Slots toggle
  // (applies immediately, written straight to the URL-backed store), Import,
  // and a refresh that targets the active view's query.
  const ToolbarActions = React.useMemo(
    () => (
      <>
        <Label
          htmlFor="appointments-show-open-slots"
          className="text-neutral-low-contrast flex cursor-pointer items-center gap-2 text-sm font-normal">
          <Switch
            id="appointments-show-open-slots"
            size="sm"
            checked={slotsToggle}
            onCheckedChange={(checked) =>
              setFilter(SHOW_OPEN_SLOTS_FIELD, checked)
            }
          />
          Slots
        </Label>
        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(next) => {
            if (next === 'table' || next === 'calendar') setView(next)
          }}
          variant="outline"
          size="sm">
          <ToggleGroupItem value="table" aria-label="Table view">
            <Table2 />
            Table
          </ToggleGroupItem>
          <ToggleGroupItem value="calendar" aria-label="Calendar view">
            <CalendarDays />
            Calendar
          </ToggleGroupItem>
        </ToggleGroup>
        <ImportDataButton entity="appointment" entityLabel="Appointment" />
        <TabRefreshButton
          onRefresh={refreshActiveView}
          isFetching={
            (view === 'calendar'
              ? calendarQuery.isFetching
              : query.isFetching) || openSlotsFetching
          }
        />
      </>
    ),
    [
      view,
      setView,
      slotsToggle,
      setFilter,
      query.isFetching,
      refreshActiveView,
      calendarQuery.isFetching,
      openSlotsFetching,
    ]
  )

  return (
    <DataTableInfinite<AppointmentRecord, unknown>
      tableId={TABLE_ID}
      columns={appointmentsColumns}
      data={data}
      filterFields={filterFields}
      manualFiltering
      manualSorting={view === 'calendar'}
      isRefetching={query.isPlaceholderData}
      isError={query.isError}
      error={query.error}
      entityLabel="appointments"
      onClearFilters={resetAllFilters}
      isLoading={
        view === 'calendar'
          ? calendarQuery.isLoading ||
            (!!deepLinkId && (query.isLoading || query.isPlaceholderData))
          : query.isLoading || query.isPlaceholderData
      }
      isFetching={query.isFetching}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={query.refetch}
      totalRows={totalRows}
      totalRowsFetched={data.length}
      getRowId={getRowId}
      defaultRowSelection={initialRowSelection}
      dataUpdatedAt={query.dataUpdatedAt}
      sheetSlot={SheetSlot}
      toolbarActions={ToolbarActions}
      chartSlot={ChartSlot}
      contentSlot={ContentSlot}
      autoColumnWidth
    />
  )
}

export function AppointmentsList() {
  const adapter = useNuqsAdapter(appointmentsSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
    // Namespace URL params (`${id}.`) so tables sharing the sidebar's carried
    // location.search don't bleed each other's same-named filters across tabs.
    namespaceUrlKeys: true,
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <AppointmentsInner />
    </DataTableStoreProvider>
  )
}
